import React, { useState, useEffect, useContext } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, ListGroup, Form, Table } from 'react-bootstrap';
import { AuthContext } from '../../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import reportService from '../../services/reportService';
import { correctHoursDisplay, formatHoursToHoursMinutes, formatDecimalHours, calculateTotalHours } from '../../utils/timeUtils';

const AdminReportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const [report, setReport] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  
  useEffect(() => {
    const fetchReportDetails = async () => {
      setLoading(true);
      try {
        const data = await reportService.getReportById(id);
        setReport(data.report);
        setComments(data.comments || []);
      } catch (err) {
        console.error('Error fetching report details:', err);
        setError('Failed to load report details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchReportDetails();
  }, [id]);
  
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    setSubmitting(true);
    try {
      const updatedComments = await reportService.addComment(id, newComment);
      setComments(updatedComments);
      setNewComment('');
    } catch (err) {
      console.error('Error adding comment:', err);
      setError('Failed to add comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !replyingTo) return;
    
    setSubmitting(true);
    try {
      console.log('Submitting reply to comment:', replyingTo);
      console.log('Parent ID being passed:', replyingTo.id);
      
      const updatedComments = await reportService.addComment(id, replyText, replyingTo.id);
      console.log('Updated comments after reply:', updatedComments);
      
      // Update the comments state and reset form
      setComments(updatedComments);
      setReplyText('');
      setReplyingTo(null);
    } catch (err) {
      console.error('Error adding reply:', err);
      setError('Failed to add reply. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  const cancelReply = () => {
    setReplyingTo(null);
    setReplyText('');
  };
  
  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="primary" onClick={() => navigate('/admin/reports')}>
          Back to Reports
        </Button>
      </Container>
    );
  }
  
  if (!report) {
    return (
      <Container className="mt-5">
        <Alert variant="warning">Report not found</Alert>
        <Button variant="primary" onClick={() => navigate('/admin/reports')}>
          Back to Reports
        </Button>
      </Container>
    );
  }
  
  return (
    <Container>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-end align-items-center">
            <Button variant="primary" onClick={() => navigate('/admin/reports')}>
              Back to Reports
            </Button>
          </div>
        </Col>
      </Row>
      
      <Row>
        <Col md={8}>
          <Card className="mb-4">
            <Card.Header as="h5" className="bg-primary text-white">
              Report for {new Date(report.date).toLocaleDateString()}
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p><strong>Date:</strong> {new Date(report.date).toLocaleDateString()}</p>
                  <p><strong>Developer:</strong> {report.user_name}</p>
                  <p><strong>Email:</strong> {report.user_email}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Hours Worked:</strong> {formatHoursToHoursMinutes(report.task_details && report.task_details.length > 0 ? calculateTotalHours(report.task_details) : report.hours_worked)}</p>
                  {/* <p><small className="text-muted">({formatDecimalHours(report.task_details && report.task_details.length > 0 ? calculateTotalHours(report.task_details) : report.hours_worked)} hours)</small></p> */}
                  <p><strong>Submitted:</strong> {new Date(report.created_at).toLocaleString()}</p>
                  {report.is_edited && (
                    <p className="text-warning"><strong><i className="bi bi-pencil"></i> This report has been edited</strong></p>
                  )}
                </Col>
              </Row>
              
              <Card className="mb-3">
                <Card.Header>Tasks Completed</Card.Header>
                <Card.Body>
                  {report.task_details && report.task_details.length > 0 ? (
                    <>
                      {/* Display tasks in a single table for now */}
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Project</th>
                            <th>Task Description</th>
                            <th>Hours</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.task_details.map((task, index) => (
                            <tr key={task.id || index}>
                              <td>{index + 1}</td>
                              <td>{task.project_name || 'No Project'}</td>
                              <td>{task.task_description}</td>
                              <td>{formatHoursToHoursMinutes(task.hours)}</td>
                            </tr>
                          ))}
                          <tr className="table-primary">
                            <td colSpan="3"><strong>Total Hours</strong></td>
                            <td>
                              <strong>{formatHoursToHoursMinutes(report.task_details && report.task_details.length > 0 ? calculateTotalHours(report.task_details) : report.hours_worked)}</strong>
                              <div className="text-muted small">({formatDecimalHours(report.task_details && report.task_details.length > 0 ? calculateTotalHours(report.task_details) : report.hours_worked)} hours)</div>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </>
                  ) : (
                    <Card.Text style={{ whiteSpace: 'pre-line' }}>
                      {report.tasks}
                    </Card.Text>
                  )}
                </Card.Body>
              </Card>
              
              {report.blockers && (
                <Card className="mb-3">
                  <Card.Header>Blockers</Card.Header>
                  <Card.Body>
                    <Card.Text style={{ whiteSpace: 'pre-line' }}>
                      {report.blockers}
                    </Card.Text>
                  </Card.Body>
                </Card>
              )}
              
              {report.tomorrow_plan && (
                <Card className="mb-3">
                  <Card.Header>Tomorrow's Plan</Card.Header>
                  <Card.Body>
                    <Card.Text style={{ whiteSpace: 'pre-line' }}>
                      {report.tomorrow_plan}
                    </Card.Text>
                  </Card.Body>
                </Card>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={4}>
          <Card className="mb-4">
            <Card.Header as="h5" className="bg-info text-white">
              Feedback & Comments
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleCommentSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Add Comment</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Enter your feedback or comment"
                  />
                </Form.Group>
                <Button 
                  type="submit" 
                  variant="primary" 
                  disabled={submitting || !newComment.trim()}
                >
                  {submitting ? 'Submitting...' : 'Submit Comment'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
          
          <Card>
            <Card.Header as="h5" className="bg-light">
              Comments History
            </Card.Header>
            <Card.Body className="p-0">
              {comments.length > 0 ? (
                <div>
                  {/* Display comments in a simple list with clear parent-child relationships */}
                  {comments.map(comment => {
                    // Only process top-level comments (no parent)
                    if (comment.parent_id !== null) return null;
                    
                    // Find direct replies to this comment
                    const directReplies = comments.filter(c => String(c.parent_id) === String(comment.id));
                    
                    return (
                      <div key={comment.id} className="mb-4 p-3">
                        {/* Main comment */}
                        <Card className={comment.user_role === 'developer' ? 'border-success' : 'border-primary'}>
                          <Card.Header className={`d-flex justify-content-between ${comment.user_role === 'developer' ? 'bg-success text-white' : 'bg-primary text-white'}`}>
                            <strong>{comment.user_name}</strong>
                            <span>
                              {comment.user_role === 'developer' && <span className="badge bg-light text-success">Developer</span>}
                              {comment.user_role === 'admin' && <span className="badge bg-light text-primary">Admin</span>}
                            </span>
                          </Card.Header>
                          <Card.Body>
                            <Card.Text>{comment.comment}</Card.Text>
                            <div className="d-flex justify-content-between align-items-center">
                              <small className="text-muted">{new Date(comment.created_at).toLocaleString()}</small>
                              {/* Only show reply button on developer comments */}
                              {comment.user_role === 'developer' && (
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  onClick={() => setReplyingTo(comment)}
                                >
                                  Reply
                                </Button>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                        
                        {/* Replies - clearly nested under the parent */}
                        {directReplies.length > 0 && (
                          <div className="ms-5 mt-2">
                            <h6 className="text-muted mb-2">Replies:</h6>
                            {directReplies.map(reply => {
                              // Find nested replies to this reply
                              const nestedReplies = comments.filter(c => String(c.parent_id) === String(reply.id));
                              
                              return (
                                <div key={reply.id}>
                                  <Card className="mb-2 border-success">
                                    <Card.Header className={`bg-${reply.user_role === 'developer' ? 'success' : 'primary'} text-white d-flex justify-content-between`}>
                                      <div>
                                        <i className="bi bi-reply-fill me-2"></i>
                                        <strong>{reply.user_name}</strong>
                                      </div>
                                      <span>
                                        {reply.user_role === 'developer' && <span className="badge bg-light text-success">Developer</span>}
                                        {reply.user_role === 'admin' && <span className="badge bg-light text-primary">Admin</span>}
                                      </span>
                                    </Card.Header>
                                    <Card.Body>
                                      <Card.Text>{reply.comment}</Card.Text>
                                      <div className="d-flex justify-content-between align-items-center">
                                        <small className="text-muted">{new Date(reply.created_at).toLocaleString()}</small>
                                        {/* Only show reply button on developer replies */}
                                        {reply.user_role === 'developer' && (
                                          <Button 
                                            variant="outline-success" 
                                            size="sm"
                                            onClick={() => setReplyingTo(reply)}
                                          >
                                            Reply
                                          </Button>
                                        )}
                                      </div>
                                    </Card.Body>
                                  </Card>
                                  
                                  {/* Nested replies (replies to replies) */}
                                  {nestedReplies.length > 0 && (
                                    <div className="ms-4 mb-3">
                                      {nestedReplies.map(nestedReply => (
                                        <Card key={nestedReply.id} className="mb-2 ms-4 border-info">
                                          <Card.Header className={`bg-${nestedReply.user_role === 'developer' ? 'success' : 'primary'} text-white d-flex justify-content-between`}>
                                            <div>
                                              <i className="bi bi-reply-all-fill me-2"></i>
                                              <strong>{nestedReply.user_name}</strong>
                                            </div>
                                            <span>
                                              {nestedReply.user_role === 'developer' && <span className="badge bg-light text-success">Developer</span>}
                                              {nestedReply.user_role === 'admin' && <span className="badge bg-light text-primary">Admin</span>}
                                            </span>
                                          </Card.Header>
                                          <Card.Body>
                                            <Card.Text>{nestedReply.comment}</Card.Text>
                                            <div className="d-flex justify-content-between align-items-center">
                                              <small className="text-muted">{new Date(nestedReply.created_at).toLocaleString()}</small>
                                              {/* Only show reply button on developer replies */}
                                              {nestedReply.user_role === 'developer' && (
                                                <Button 
                                                  variant="outline-info" 
                                                  size="sm"
                                                  onClick={() => setReplyingTo(nestedReply)}
                                                >
                                                  Reply
                                                </Button>
                                              )}
                                            </div>
                                          </Card.Body>
                                        </Card>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center text-muted p-4">
                  No comments yet
                </div>
              )}
            </Card.Body>
            
            {/* Reply form */}
            {replyingTo && (
              <Card.Footer className="p-4 mt-3">
                <div className="mb-4 p-3 bg-light rounded">
                  <h6 className="mb-3">Reply to {replyingTo.user_name}'s comment:</h6>
                  <blockquote className="p-3 border-start border-3 border-primary">
                    <p className="mb-0">{replyingTo.comment}</p>
                  </blockquote>
                </div>
                <Form onSubmit={handleReplySubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label>Your Reply</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply here"
                      required
                      className="mb-3"
                    />
                  </Form.Group>
                  <div className="d-flex justify-content-end gap-3">
                    <Button
                      variant="secondary"
                      onClick={cancelReply}
                      disabled={submitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={submitting || !replyText.trim()}
                    >
                      {submitting ? 'Submitting...' : 'Submit Reply'}
                    </Button>
                  </div>
                </Form>
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminReportDetail;
